import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Clock3, ShieldAlert, X } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const statusStyles = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  denied: 'border-red-300 bg-red-50 text-red-700',
  expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const policyStyles = {
  internal_authentication_ready: 'text-emerald-700',
  internal_evidence_ready: 'text-zinc-700',
  internal_foundation: 'text-zinc-700',
  not_ready: 'text-amber-800',
  blocked: 'text-red-700',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function requestStatusLabel(status) {
  return String(status || 'pending').replaceAll('_', ' ');
}

function policyStatusLabel(status) {
  return String(status || 'not_ready').replaceAll('_', ' ');
}

function RequestStatus({ status }) {
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${statusStyles[status] || statusStyles.pending}`}>{requestStatusLabel(status)}</span>;
}

function approvalSummaryLabel(context) {
  if (!context) return 'Admin override approval';
  if (context.approval_mode === 'consumer_acknowledged') return 'Consumer acknowledgement';
  if (context.device_bound) return 'Device-bound approval';
  return context.policy_override ? 'Admin override approval (policy override)' : 'Admin override approval';
}

function timeRemaining(expiresAt) {
  const milliseconds = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return 'Expired';
  const minutes = Math.ceil(milliseconds / 60000);
  return minutes === 1 ? 'Expires in 1 min' : `Expires in ${minutes} min`;
}

export default function InternalAuthenticatorPreview() {
  const [searchParams] = useSearchParams();
  const openedRequestId = searchParams.get('request_id') || '';
  const [requests, setRequests] = useState([]);
  const [policiesByAccount, setPoliciesByAccount] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [entryNotice, setEntryNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingDenial, setPendingDenial] = useState(null);
  const [denialReason, setDenialReason] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(openedRequestId);
  const [returnTargetRequest, setReturnTargetRequest] = useState(null);
  const openedRequestHandledRef = useRef(false);

  const load = useCallback(async (filter = statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter) params.set('status', filter);
      const { data } = await api.get(`/digital-key-accounts/admin/digital-key-login-requests?${params.toString()}`);
      const requestList = data.requests || [];
      const accountIds = [...new Set(requestList.map((request) => request.digital_key_account_id).filter(Boolean))];
      const policyResponses = await Promise.all(accountIds.map(async (accountId) => {
        const { data: policyData } = await api.get(`/digital-key-accounts/admin/digital-key-accounts/${accountId}/assurance-policy`);
        return [accountId, policyData.policy || null];
      }));
      setRequests(requestList);
      setPoliciesByAccount(Object.fromEntries(policyResponses));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Internal login requests could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openedRequestId || openedRequestHandledRef.current || requests.length === 0) return;
    const match = requests.find((request) => request.id === openedRequestId);
    openedRequestHandledRef.current = true;
    if (match) {
      setSelectedRequestId(match.id);
      setEntryNotice(`Opened from OIDC Lab for request ${match.id}.`);
      return;
    }
    setEntryNotice('Opened from OIDC Lab, but the requested login is not in the current queue yet.');
  }, [openedRequestId, requests]);

  const pendingRequests = useMemo(() => requests.filter((request) => request.status === 'pending'), [requests]);
  const finalizedRequests = useMemo(() => requests.filter((request) => request.status !== 'pending'), [requests]);
  const requestCounts = useMemo(() => ({
    pending: requests.filter((request) => request.status === 'pending').length,
    approved: requests.filter((request) => request.status === 'approved').length,
    denied: requests.filter((request) => request.status === 'denied').length,
    total: requests.length,
  }), [requests]);
  const selectedRequest = useMemo(() => {
    if (selectedRequestId) return requests.find((request) => request.id === selectedRequestId) || null;
    return pendingRequests[0] || finalizedRequests[0] || null;
  }, [finalizedRequests, pendingRequests, requests, selectedRequestId]);
  const policyForRequest = (request) => policiesByAccount[request.digital_key_account_id] || null;

  const changeFilter = (filter) => {
    setStatusFilter(filter);
  };

  const approve = async (request) => {
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-login-requests/${request.id}/approve`);
      setNotice({ tone: 'success', text: 'Internal login request approved.' });
      setReturnTargetRequest(request);
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The internal login request could not be approved.' });
    } finally {
      setSubmitting(false);
    }
  };

  const deny = async (event) => {
    event.preventDefault();
    if (!pendingDenial || !denialReason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      await api.post(`/digital-key-accounts/admin/digital-key-login-requests/${pendingDenial.id}/deny`, { reason: denialReason.trim() });
      setPendingDenial(null);
      setDenialReason('');
      setNotice({ tone: 'success', text: 'Internal login request denied.' });
      setReturnTargetRequest(pendingDenial);
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The internal login request could not be denied.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="internal-authenticator-preview-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Authenticator Preview" showDashboard={false}/>

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Platform</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Internal Authenticator Preview</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-600">Internal simulation of future idem authenticator approval. No public login, OIDC, tokens, FAS/CSAM integration, official LoA, or regulated identifier processing is implemented.</p>
        <Link to="/id/authenticator" className="mt-4 inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open idem authenticator app preview</Link>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/internal/identities" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open identities</Link>
          <Link to="/internal/digital-key-accounts" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open digital key accounts</Link>
          <Link to="/internal/oidc-lab" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open OIDC lab</Link>
        </div>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5}/>
          <div><p className="font-medium">Internal sandbox only</p><p className="mt-1">This page simulates user/device approval for internal Digital Key login requests. It does not create public sessions, authorization codes, tokens, OIDC responses, FAS/CSAM integration, official LoA, or regulated identifier processing.</p></div>
        </div>
      </section>

      {entryNotice && <div className="mt-6 border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">{entryNotice}</div>}
      {notice && <div className={`mt-6 border px-4 py-3 text-sm ${notice.tone === 'success' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-red-300 bg-red-50 text-red-700'}`}>{notice.text}</div>}

      {returnTargetRequest && (
        <section className="mt-6 border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          <p className="font-medium">Return to OIDC Lab</p>
          <p className="mt-1 text-sky-800">Send this request back to the lab for finalization after the approval decision.</p>
          <div className="mt-3">
            <Link
              to={`/internal/oidc-lab?request_id=${encodeURIComponent(returnTargetRequest.id)}${returnTargetRequest.identity_id ? `&identity_id=${encodeURIComponent(returnTargetRequest.identity_id)}` : ''}${returnTargetRequest.digital_key_account_id ? `&account_id=${encodeURIComponent(returnTargetRequest.digital_key_account_id)}` : ''}`}
              className="inline-flex items-center gap-1.5 border border-sky-200 bg-white px-3 py-2 text-sky-800 hover:border-sky-300"
            >
              Return to OIDC Lab
            </Link>
          </div>
        </section>
      )}

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Pending requests</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900">{requestCounts.pending}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Approved</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-emerald-700">{requestCounts.approved}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Denied</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-red-700">{requestCounts.denied}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total reviewed</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900">{requestCounts.total}</p>
        </div>
      </section>

      {selectedRequest && (
        <section className="mt-6 border border-zinc-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">Selected request</h2>
              <p className="mt-1 text-sm text-zinc-600">Review only safe request metadata. No raw evidence or secrets are shown here.</p>
            </div>
            <RequestStatus status={selectedRequest.status}/>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="border border-zinc-100 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Request ID</p>
              <p className="mt-1 break-all font-mono text-xs text-zinc-700">{selectedRequest.id}</p>
            </div>
            <div className="border border-zinc-100 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Identity</p>
              <p className="mt-1 text-sm text-zinc-700">{selectedRequest.identity_display_name || selectedRequest.identity_ref || '—'}</p>
              <p className="mt-1 font-mono text-[10px] text-zinc-500">{selectedRequest.identity_id || '—'}</p>
            </div>
            <div className="border border-zinc-100 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Relying party</p>
              <p className="mt-1 text-sm text-zinc-700">{selectedRequest.request_context?.lab_client_id || 'idemgo-rp-simulator'}</p>
              <p className="mt-1 font-mono text-[10px] text-zinc-500">{selectedRequest.request_context?.channel || 'internal_admin_sandbox'}</p>
            </div>
            <div className="border border-zinc-100 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested claims</p>
              <p className="mt-1 text-xs text-zinc-600">{selectedRequest.requested_claims?.join(', ') || 'No claims requested'}</p>
            </div>
            <div className="border border-zinc-100 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Timestamps</p>
              <p className="mt-1 text-xs text-zinc-600">Created {formatDate(selectedRequest.created_at)}</p>
              <p className="mt-1 text-xs text-zinc-600">Updated {formatDate(selectedRequest.updated_at)}</p>
            </div>
            <div className="border border-zinc-100 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Approval context</p>
              <p className="mt-1 text-xs text-zinc-600">{selectedRequest.status === 'approved' ? (selectedRequest.approved_device_label || 'Device-bound or admin override approval') : 'Awaiting authenticator review'}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {pendingRequests.slice(0, 3).map((request) => (
              <button
                key={request.id}
                type="button"
                onClick={() => setSelectedRequestId(request.id)}
                className="border border-zinc-200 px-3 py-2 text-xs text-zinc-700 hover:border-zinc-400"
              >
                Inspect {request.identity_display_name || request.identity_ref || request.id}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="mt-6 border border-zinc-200 bg-white p-5" aria-labelledby="authenticator-pending-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="authenticator-pending-title" className="font-display text-lg font-semibold">Pending requests</h2>
            <p className="mt-1 text-sm text-zinc-600">Approve or deny a pending internal request as a simulation only.</p>
          </div>
          <label className="text-sm text-zinc-600">
            <span className="sr-only">Request status</span>
            <select value={statusFilter} onChange={(event) => changeFilter(event.target.value)} className="border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
        </div>

        {loading ? <p className="mt-4 text-sm text-zinc-500">Loading internal login requests…</p> : error ? <p className="mt-4 text-sm text-red-700">{error}</p> : pendingRequests.length ? (
          <div className="mt-4 grid gap-3 xl:grid-cols-2">
            {pendingRequests.map((request) => (
              <article key={request.id} className={`p-4 ${selectedRequestId === request.id ? 'border border-zinc-900 bg-zinc-50/50 ring-1 ring-zinc-900/10' : 'border border-zinc-200'}`}>
                {(() => {
                  const policy = policyForRequest(request);
                  const policyBlocksOverride = ['blocked', 'not_ready'].includes(policy?.status);
                  const policyReason = policy?.blockers?.[0]?.replaceAll('_', ' ');
                  return <>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-zinc-600" title={request.id}>{request.id}</p>
                    <p className="mt-2 text-sm font-medium text-zinc-900">{request.identity_display_name || request.identity_ref}</p>
                    <p className="mt-1 text-xs text-zinc-500">{request.identity_ref}</p>
                  </div>
                  <RequestStatus status={request.status}/>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Account</dt><dd className="mt-1 font-mono text-xs text-zinc-700">{request.account_status || '—'}</dd></div>
                  <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Assurance</dt><dd className="mt-1 text-xs text-zinc-700">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</dd></div>
                  <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Created</dt><dd className="mt-1 text-xs text-zinc-700">{formatDate(request.created_at)}</dd></div>
                  <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Updated</dt><dd className="mt-1 text-xs text-zinc-700">{formatDate(request.updated_at)}</dd></div>
                  <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expiry</dt><dd className="mt-1 text-xs text-zinc-700">{formatDate(request.expires_at)}<span className="ml-1 text-zinc-500">({timeRemaining(request.expires_at)})</span></dd></div>
                  <div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Relying party</dt><dd className="mt-1 text-xs text-zinc-700">{request.request_context?.lab_client_id || 'idemgo-rp-simulator'}</dd></div>
                </dl>
                  <div className="mt-4 border-t border-zinc-100 pt-3"><p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested claims</p><p className="mt-1 text-xs text-zinc-600">{request.requested_claims?.join(', ') || 'No claims requested'}</p>{request.request_context?.note && <p className="mt-2 text-xs text-zinc-500">Note: {request.request_context.note}</p>}</div>
                {policy && <p className={`mt-3 text-xs ${policyStyles[policy.status] || 'text-zinc-600'}`}>Policy: {policyStatusLabel(policy.status)}{policyReason ? ` — ${policyReason}` : ''}</p>}
                {policy?.warnings?.length ? <p className="mt-1 text-xs text-amber-800">Warning: {policy.warnings[0].replaceAll('_', ' ')}</p> : null}
                {policyBlocksOverride && <p className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">This request cannot be approved, including by internal admin override, until the blocking policy condition is resolved.</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setSelectedRequestId(request.id); approve(request); }} disabled={submitting || policyBlocksOverride} className="inline-flex items-center gap-1.5 border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:border-emerald-500 disabled:opacity-50"><Check className="h-3.5 w-3.5" strokeWidth={1.5}/> Approve internal login request</button>
                  <button type="button" onClick={() => { setSelectedRequestId(request.id); setPendingDenial(request); setDenialReason(''); }} disabled={submitting} className="inline-flex items-center gap-1.5 border border-red-200 px-3 py-2 text-sm text-red-700 hover:border-red-400 disabled:opacity-50"><X className="h-3.5 w-3.5" strokeWidth={1.5}/> Deny internal login request</button>
                </div>
                  </>;
                })()}
              </article>
            ))}
          </div>
        ) : <p className="mt-4 text-sm text-zinc-500">No pending internal login requests. Create one from an active Digital Key Account on an Identity Detail page.</p>}

        {pendingDenial && (
          <form onSubmit={deny} className="mt-4 border-t border-zinc-100 pt-4">
            <p className="text-sm font-medium text-zinc-800">Deny internal login request</p>
            <label className="mt-3 block text-xs font-mono uppercase tracking-widest text-zinc-500" htmlFor="authenticator-denial-reason">Reason</label>
            <textarea id="authenticator-denial-reason" value={denialReason} onChange={(event) => setDenialReason(event.target.value)} required maxLength={500} rows={3} className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => { setPendingDenial(null); setDenialReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button><button type="submit" disabled={!denialReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Deny request</button></div>
          </form>
        )}
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5" aria-labelledby="authenticator-finalized-title">
        <div className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5}/><h2 id="authenticator-finalized-title" className="font-display text-lg font-semibold">Recent finalized requests</h2></div>
        {loading ? <p className="mt-4 text-sm text-zinc-500">Loading internal login requests…</p> : finalizedRequests.length ? (
          <div className="mt-4 overflow-x-auto"><table className="min-w-full text-sm"><thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500"><tr><th className="py-2 pr-3 font-medium">Request</th><th className="py-2 pr-3 font-medium">Identity</th><th className="py-2 pr-3 font-medium">Relying party</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">Claims</th><th className="py-2 pr-3 font-medium">Approval context</th><th className="py-2 pr-3 font-medium">Created</th><th className="py-2 font-medium">Finalized</th></tr></thead><tbody>{finalizedRequests.map((request) => <tr key={request.id} className={`border-b border-zinc-100 last:border-b-0 ${selectedRequestId === request.id ? 'bg-zinc-50/50' : ''}`}><td className="max-w-40 truncate py-3 pr-3 font-mono text-xs text-zinc-600" title={request.id}><button type="button" onClick={() => setSelectedRequestId(request.id)} className="hover:underline">{request.id}</button></td><td className="py-3 pr-3"><Link to={`/internal/identities/${request.identity_id}`} className="text-zinc-700 hover:text-black hover:underline">{request.identity_display_name || request.identity_ref}</Link><div className="mt-1 font-mono text-[10px] text-zinc-500">{request.identity_id}</div></td><td className="py-3 pr-3 text-xs text-zinc-600">{request.request_context?.lab_client_id || 'idemgo-rp-simulator'}</td><td className="py-3 pr-3"><RequestStatus status={request.status}/></td><td className="py-3 pr-3 text-xs text-zinc-600">{request.requested_claims?.length || 0}</td><td className="py-3 pr-3 text-xs text-zinc-600">{request.status === 'approved' ? <>{request.approval_context_summary ? <><p>{approvalSummaryLabel(request.approval_context_summary)}</p><p className="mt-1 text-zinc-500">{request.approval_context_summary.approval_context_version} · {request.approval_context_summary.approved_device_label || 'No device selected'} · {String(request.approval_context_summary.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</p></> : (request.approved_device_label || 'Admin override approval')}</> : '—'}</td><td className="whitespace-nowrap py-3 pr-3 text-xs text-zinc-500">{formatDate(request.created_at)}</td><td className="whitespace-nowrap py-3 text-xs text-zinc-500">{formatDate(request.approved_at || request.denied_at || request.expired_at || request.cancelled_at)}</td></tr>)}</tbody></table></div>
        ) : <p className="mt-4 text-sm text-zinc-500">No finalized login requests yet.</p>}
      </section>
    </div>
  );
}
